import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { LMSpecService } from '@services/lm-spec.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { SohoAlertDirective, SohoSpinboxComponent } from 'ids-enterprise-ng';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';
import { filter } from 'cypress/types/bluebird';

@Component({
  selector:    'lm-filter-builder-row',
  templateUrl: './lm-filter-builder-row.component.html',
  styleUrls:   [ './lm-filter-builder-row.component.scss' ]
})
export class LMFilterBuilderRowComponent implements OnInit {

  @ViewChild(SohoSpinboxComponent) spinboxComponent: SohoSpinboxComponent;
  @ViewChild(SohoAlertDirective) alert: SohoAlertDirective;

  @Input() filterRow: FilterRow;
  @Input() businessClass: string;
  @Input() includeAuditFields: boolean;
  @Input() lookupUrl: string;
  @Input() index: number;
  @Input() fieldSearchUrl: string;
  @Input() lplValidationUrl: string;

  @Output() onFieldLookUpEvent = new EventEmitter<LMFieldLookUpEvent>();
  @Output() onRemoveRowEvent = new EventEmitter<LMFilterBuilderRemoveRowEvent>();

  conditions: Array<any>;
  hasPredicate: boolean;
  literalId: string;
  checkboxId: string;
  operatorId: string;

  constructor(private lmSpecService: LMSpecService,
    private lmLoggerService: LMLoggerService,
    private changeDetectorRef: ChangeDetectorRef,
    private lmUniqueIdService: LMUniqueIdService) {
  }

  ngOnInit() {
    this.setupConditionSelection('subject');
    const unique: number = this.filterRow?.uniqueIdentifier ? this.filterRow?.uniqueIdentifier : this.index;
    this.literalId = this.lmUniqueIdService.uniqueId([ 'literal', unique.toString() ]);
    this.checkboxId = this.lmUniqueIdService.uniqueId([ 'checkbox', unique.toString() ]);
    this.operatorId = this.lmUniqueIdService.uniqueId([ 'operator', unique.toString() ]);
    this.hidePredicate();
  }

  setupConditionSelection(source?: string) {
    if (source === 'subject') {
      this.filterRow.condition = '';
    }

    this.conditions = [];

    if (this.filterRow.subject.type === 'TEXT'
      || this.filterRow.subject.type === 'GROUP'
      || this.filterRow.subject.type === 'BINARY'
      || this.filterRow.subject.type === 'BINARY_DOCUMENT'
      || this.filterRow.subject.type === 'BINARY_OBJECT'
      || this.filterRow.subject.type === 'UNIQUEID'
      || this.filterRow.subject.type === 'ACTOR'
      || this.filterRow.subject.type === 'ARRAY'
      || this.filterRow.subject.type === 'BUSCLASS'
      || this.filterRow.subject.type === 'CONTEXT') {
      this.conditions.push({ value: 'like', label: 'Like' });
    }

    if (this.filterRow.subject.type === 'TEXT'
      || this.filterRow.subject.type === 'GROUP'
      || this.filterRow.subject.type === 'BINARY'
      || this.filterRow.subject.type === 'BINARY_DOCUMENT'
      || this.filterRow.subject.type === 'BINARY_OBJECT'
      || this.filterRow.subject.type === 'UNIQUEID'
      || this.filterRow.subject.type === 'ACTOR'
      || this.filterRow.subject.type === 'ARRAY'
      || this.filterRow.subject.type === 'BUSCLASS'
      || this.filterRow.subject.type === 'CONTEXT'
      || this.filterRow.subject.type === 'NUMBER'
      || this.filterRow.subject.type === 'DATE'
      || this.filterRow.subject.type === 'TIME'
      || this.filterRow.subject.type === 'TIMESTAMP'
      || this.filterRow.subject.type === 'CHANGESTAMP') {
      this.conditions.push({ value: 'equalTo', label: 'Equal to' });
      this.conditions.push({ value: 'notEqualTo', label: 'Not equal to' });
      this.conditions.push({ value: 'entered', label: 'Entered' });
      this.conditions.push({ value: 'notEntered', label: 'Not entered' });
    }

    if (this.filterRow.subject.type === 'NUMBER'
      || this.filterRow.subject.type === 'DATE'
      || this.filterRow.subject.type === 'TIME'
      || this.filterRow.subject.type === 'TIMESTAMP'
      || this.filterRow.subject.type === 'CHANGESTAMP') {
      this.conditions.push({ value: 'greaterThan', label: 'Greater than' });
      this.conditions.push({ value: 'greaterThanOrEqualTo', label: 'Greater than or equal to' });
      this.conditions.push({ value: 'lessThan', label: 'Less than' });
      this.conditions.push({ value: 'lessThanOrEqualTo', label: 'Less than or equal to' });
    }

    if (this.filterRow.subject.type === 'BOOLEAN'
     || this.filterRow.subject.type === 'CONDITION'
     || this.filterRow.subject.type === 'STATE') {
      this.conditions.push({ value: 'true', label: 'True' });
      this.conditions.push({ value: 'false', label: 'False' });
    }

    if (this.filterRow.subject.type === 'OTO_RELATION' || this.filterRow.subject.type === 'OTM_RELATION') {
      this.conditions.push({ value: 'exist', label: 'Exist' });
      this.conditions.push({ value: 'doesNotExist', label: 'Does not exist' });
    }
  }

  onRemoveButtonEvent() {
    this.filterRow.index = this.index;
    this.onRemoveRowEvent.emit({ filterRow: this.filterRow });
  }

  handleFieldLookUpEvent(event: LMFieldLookUpEvent, source: string) {
    if (event?.qualifier && source === 'subject') {
      this.filterRow.subject.type = event.qualifier.type;
    }

    this.setupConditionSelection(source);
    this.hidePredicate();

    if (source === 'subject') {
      this.updateAlert();
    }

    this.changeDetectorRef.detectChanges();
  }

  hidePredicate() {
    this.hasPredicate = this.filterRow.subject.type === 'BOOLEAN'
      || this.filterRow.subject.type === 'OTO_RELATION'
      || this.filterRow.subject.type === 'OTM_RELATION'
      || this.filterRow.subject.type === 'CONDITION'
      || this.filterRow.subject.type === 'STATE'
      || this.filterRow.condition === 'entered'
      || this.filterRow.condition === 'notEntered';

    if (this.hasPredicate) {
      this.filterRow.predicate.text = '';
      this.filterRow.predicate.type = '';
    }
  }

  clearField() {
    this.filterRow.predicate.text = '';
    this.filterRow.predicate.type = 'TEXT';

    // need in timeout so the literal alert is added to the dom
    setTimeout(() => this.updateAlert());
  }

  private updateAlert() {
    if (this.alert) {
      if (this.filterRow.subject.type === 'DATE') {
        this.alert.type = 'info';
        this.alert.message = Soho.Locale.translate('LiteralDateFormatAlert');
      } else {
        this.alert.removeAllMessages();
      }
    }
  }
}
